/**
 * @file Tasks service — CRUD operations on crm_tasks store (pbf_crm_tasks)
 * Compatible with Deploy Z task data model for bidirectional sync.
 */

import { store, load } from '@state/store.js';

// =============================================
// TASK TAGS (matches Deploy Z exactly)
// =============================================

export const TASK_TAGS = {
  'job':       { label: 'Job',       hex: '#D4AF37', color: 'badge-gold' },
  'followup':  { label: 'Follow-up', hex: '#22C55E', color: 'badge-green' },
  'quote':     { label: 'Quote',     hex: '#A855F7', color: 'badge-purple' },
  'urgent':    { label: 'Urgent',    hex: '#EF4444', color: 'badge-red' },
  'admin':     { label: 'Admin',     hex: '#6B7280', color: 'badge-gray' },
  'none':      { label: 'No Tag',    hex: '#6B7280', color: 'badge-gray' },
};

// =============================================
// CRUD OPERATIONS
// =============================================

/**
 * Load all tasks from store (exact key: crm_tasks -> pbf_crm_tasks).
 */
export function getTasks() {
  return load('crm_tasks', []);
}

/**
 * Get a single task by ID.
 */
export function getTaskById(id) {
  return getTasks().find(t => t.id === id) || null;
}

/**
 * Add a new task.
 */
export function addTask(text, tag = 'none', priority = 'normal', date = null) {
  const tasks = getTasks();
  let maxOrder = 0;
  tasks.forEach(t => { if ((t.sortOrder || 0) >= maxOrder) maxOrder = (t.sortOrder || 0) + 1; });

  const newTask = {
    id: 'task_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8),
    text,
    subdesc: '',
    done: false,
    origin: 'today',
    tag: tag || 'none',
    date: date || new Date().toISOString().split('T')[0],
    priority: priority || 'normal',
    completedAt: null,
    createdAt: Date.now(),
    gTaskId: null,
    sortOrder: maxOrder,
    subtasks: [],
  };
  tasks.push(newTask);
  store('crm_tasks', tasks);
  return newTask;
}

/**
 * Toggle a task's done status.
 */
export function toggleTask(id) {
  const tasks = getTasks();
  const t = tasks.find(x => x.id === id);
  if (!t) return null;
  t.done = !t.done;
  if (t.done) {
    t.completedAt = Date.now();
    if (!t.origin) {
      const today = new Date().toISOString().split('T')[0];
      t.origin = t.date > today ? 'upcoming' : 'today';
    }
  } else {
    t.completedAt = null;
  }
  store('crm_tasks', tasks);
  return t;
}

/**
 * Delete a task by ID.
 */
export function deleteTask(id) {
  const tasks = getTasks().filter(x => x.id !== id);
  store('crm_tasks', tasks);
}

/**
 * Update a task with partial data.
 */
export function updateTask(id, updates) {
  const tasks = getTasks();
  const t = tasks.find(x => x.id === id);
  if (!t) return null;
  Object.assign(t, updates);
  store('crm_tasks', tasks);
  return t;
}

/**
 * Get today's tasks (due today or earlier, not done).
 */
export function getTodaysTasks() {
  const today = new Date().toISOString().split('T')[0];
  return getTasks().filter(t => !t.done && t.date <= today);
}

/**
 * Get upcoming tasks (due after today, not done).
 */
export function getUpcomingTasks() {
  const today = new Date().toISOString().split('T')[0];
  return getTasks().filter(t => !t.done && t.date > today);
}

/**
 * Get completed tasks.
 */
export function getCompletedTasks() {
  return getTasks().filter(t => t.done);
}

/**
 * Toggle a subtask's done status.
 */
export function toggleSubtask(taskId, subtaskIdx) {
  const tasks = getTasks();
  const t = tasks.find(x => x.id === taskId);
  if (!t || !t.subtasks || !t.subtasks[subtaskIdx]) return null;
  t.subtasks[subtaskIdx].done = !t.subtasks[subtaskIdx].done;
  store('crm_tasks', tasks);
  return t;
}

/**
 * Set task priority.
 */
export function setTaskPriority(id, priority) {
  return updateTask(id, { priority });
}
